import { isNumberString } from "@corca/core";
import { computed, makeObservable } from "mobx";

import { BaseNodeModel } from "@/models/nodes/BaseNodeModel";
import { isNodeHighlighted } from "@/models/nodes/utils";
import { isPowerOperator, isSubscript } from "@/models/utils/semanticChecks";

import { UnitNodeModel } from "./UnitNodeModel";

import type { DocumentModel } from "@/models/DocumentModel";
import type { NodeModel, NodeId } from "@/models/nodes/types";
import type {
  VisitableNode,
  InlineEditableNode,
  NodeVisitor,
} from "@/models/nodes/visitors/NodeVisitor";
import type { NumberNodeProps, NumberNodeConstructorProps } from "@corca/data";

export class NumberNodeModel
  extends BaseNodeModel<NumberNodeProps>
  implements VisitableNode, InlineEditableNode
{
  static type = "number" as const;

  readonly inlineEditable = true as const;

  isMergeable = true;

  isSplittable = true;

  static validateValue(this: void, value: string, allowIncomplete?: boolean) {
    if (value.startsWith("+") || value.startsWith("-")) return false;

    const isValid = isNumberString(value);
    if (!allowIncomplete) return isValid;

    if (isValid) return true;
    if ((value.includes("e") || value.includes("E")) && isNumberString(`${value}0`)) return true;

    return false;
  }

  type = NumberNodeModel.type;

  validateValue = NumberNodeModel.validateValue;

  protected fillRequiredProps(props: NumberNodeConstructorProps): NumberNodeProps {
    this.reportMissingProps(props, ["value"]);
    return { ...props, value: props.value ?? "" };
  }

  constructor(...args: ConstructorParameters<typeof BaseNodeModel<NumberNodeConstructorProps>>) {
    super(...args);
    makeObservable(this, {
      isHighlighted: computed,
    });
  }

  static create(doc: DocumentModel, value: string, nodes: NodeModel[] = [], id?: NodeId) {
    return new NumberNodeModel(doc, id, { value }, nodes);
  }

  get isEditable(): boolean {
    return true;
  }

  get value(): string {
    return this.props.value;
  }

  set value(value: string) {
    this.updateProps({ value });
  }

  get unitNode() {
    return this.children.find((n) => n.is(UnitNodeModel));
  }

  get power() {
    return this.children.find(isPowerOperator);
  }

  get subscript() {
    return this.children.find(isSubscript);
  }

  get isHighlighted(): boolean {
    return isNodeHighlighted(this.doc, this);
  }

  accept<R>(visitor: NodeVisitor<R>): R {
    return visitor.visitNumberNode(this);
  }
}
